import React, { useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import Pane from 'components/Common/Pane'
import Group from 'components/utils/Group'
import Image from 'components/Common/Image'
import Heading from 'components/Luxkit/Typography/Heading'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import BodyText from 'components/Luxkit/Typography/BodyText'
import StructureLabel from 'components/Luxkit/Label/StructureLabel'
import BookingsCountLabel from 'components/Common/Labels/BookingsCountLabel'
import RoomsRemainingLabel from 'components/Common/Labels/RoomsRemainingLabel'
import OfferUrgencyRoomCount from 'components/Common/Labels/OfferUrgencyRoomCount'
import FlashCancellationPolicies from 'components/Common/CancellationPolicies/Flash/FlashCancellationPolicies'
import LoyaltyPoints from 'loyalty/components/LoyaltyPoints'
import { OptimizelyExperiments } from 'constants/optimizely'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { isAvailableForPurchase, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import { generateLoyaltyPointsCalculatorBundledFlightOptions, generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import MultipleRoomsInclusions from '../MultipleRoomsInclusions'
import MobileMultipleRoomsBookingPriceDetails from './MobileMultipleRoomsBookingPriceDetails'

const RoomContainer = styled(Pane)`
  padding: ${rem(20)};
`

interface Props {
  offer: App.Offer;
  checkIn: string;
  room: App.RoomOccupants;
  roomPosition: number;
  availableRoomCount?: number;
  isFlightsSelected: boolean;
  availableForPurchase: boolean;
  packageView: OfferPackageOptionView;
}

function MobileMultipleRoomsBookingRoomOption(props: Props) {
  const { offer, checkIn, room, roomPosition, availableRoomCount, availableForPurchase, isFlightsSelected, packageView } = props

  const firstRoomImageId = packageView.package.roomType?.images?.[0]?.id

  const croPopularOptionBookingRemaining = !!useOptimizelyExperiment(OptimizelyExperiments.croPopularOptionBookingRemaining, !!availableRoomCount && !!checkIn)

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorHotelOptions(offer, { packageView }),
    generateLoyaltyPointsCalculatorBundledFlightOptions(offer, { packageView, skip: !isFlightsSelected }),
  ], [offer, packageView, isFlightsSelected])

  return (
    <RoomContainer>
      <Group direction="vertical" gap={16}>
        <Group direction="vertical" gap={8}>
          <Group direction="horizontal" horizontalAlign="space-between" gap={16}>
            <Group direction="vertical" gap={12}>
              <StructureLabel variant="dark">Room {roomPosition}</StructureLabel>
              <div>
                <Heading variant="heading4">{packageView.package.roomType?.name}</Heading>
                <BodyText variant="medium" colour="neutral-three">{formatOccupantsShort(room)}</BodyText>
              </div>
            </Group>
            <Image id={firstRoomImageId} width={80} height={80} />
          </Group>
          <LabelGroup>
            {checkIn && !croPopularOptionBookingRemaining && <RoomsRemainingLabel roomCount={packageView.availableRooms ?? 0} threshold="rate" />}
            {checkIn && croPopularOptionBookingRemaining && availableRoomCount && <OfferUrgencyRoomCount availableRoomCount={availableRoomCount}/> }
            <BookingsCountLabel bookings={packageView.package.roomType?.stats.booked ?? 0} threshold="room" />
          </LabelGroup>
          {availableForPurchase && <FlashCancellationPolicies
            checkInDate={checkIn}
            propertyTimezoneOffset={offer.property?.timezoneOffset}
            cancellationPolicy={packageView.package.roomRate?.cancellationPolicy}
          />}
          <LoyaltyPoints
            calculationRequests={pointsEarnCalculationRequests}
            calculationType={(!checkIn || isFlightsSelected) ? 'estimate' : 'exact'}
          />
          <MultipleRoomsInclusions offer={offer} packageOption={packageView} />
        </Group>
        <MobileMultipleRoomsBookingPriceDetails room={room} packageOption={packageView} />
      </Group>
    </RoomContainer>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  offer: state.offer!,
  rooms: state.rooms,
  checkIn: state.checkInDate,
  isFlightsSelected: state.flights,
  availableForPurchase: isAvailableForPurchase(state, appState),
}), MobileMultipleRoomsBookingRoomOption)
